export const MANUFACTURING_SPECIFICATION_KEYS = [
    "color",
    "size",
    "text",
    "designMediaUuid",
] as const;

export type ManufacturingSpecificationKey = typeof MANUFACTURING_SPECIFICATION_KEYS[number];

export type PublicManufacturingSpecification = Partial<Record<ManufacturingSpecificationKey, string>>;

export type PublicManufacturingProfile = {
    profileAvailable: boolean;
    allowedKeys: ManufacturingSpecificationKey[];
    requiredKeys: ManufacturingSpecificationKey[];
};

export type PublicManufacturingDesignMedia = {
    designMediaUuid: string;
    mimeType: "image/jpeg" | "image/png" | "image/webp";
    size: number;
    width: number;
    height: number;
};

export type ManufacturingSpecificationValidation = {
    invalidKeys: ManufacturingSpecificationKey[];
    missingKeys: ManufacturingSpecificationKey[];
    valid: boolean;
};

const EMPTY_PROFILE: PublicManufacturingProfile = {
    profileAvailable: false,
    allowedKeys: [],
    requiredKeys: [],
};

const DESIGN_MEDIA_UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function isManufacturingSpecificationKey(value: unknown): value is ManufacturingSpecificationKey {
    return typeof value === "string"
        && (MANUFACTURING_SPECIFICATION_KEYS as readonly string[]).includes(value);
}

function uniqueSpecificationKeys(value: unknown): ManufacturingSpecificationKey[] {
    if (!Array.isArray(value)) {
        return [];
    }

    return [...new Set(value.filter(isManufacturingSpecificationKey))];
}

export function publicManufacturingProfileFromValue(value: unknown): PublicManufacturingProfile | null {
    if (!isRecord(value) || typeof value.profileAvailable !== "boolean") {
        return null;
    }

    if (!value.profileAvailable) {
        return EMPTY_PROFILE;
    }

    if (
        !Array.isArray(value.allowedKeys)
        || !Array.isArray(value.requiredKeys)
        || value.allowedKeys.some((key) => !isManufacturingSpecificationKey(key))
        || value.requiredKeys.some((key) => !isManufacturingSpecificationKey(key))
    ) {
        return null;
    }

    const allowedKeys = uniqueSpecificationKeys(value.allowedKeys);
    const requiredKeys = uniqueSpecificationKeys(value.requiredKeys);

    if (requiredKeys.some((key) => !allowedKeys.includes(key))) {
        return null;
    }

    return {
        profileAvailable: true,
        allowedKeys,
        requiredKeys,
    };
}

export function publicManufacturingDesignMediaFromValue(value: unknown): PublicManufacturingDesignMedia | null {
    if (
        !isRecord(value)
        || typeof value.designMediaUuid !== "string"
        || !DESIGN_MEDIA_UUID_PATTERN.test(value.designMediaUuid)
        || (value.mimeType !== "image/jpeg" && value.mimeType !== "image/png" && value.mimeType !== "image/webp")
        || typeof value.size !== "number"
        || typeof value.width !== "number"
        || typeof value.height !== "number"
        || !Number.isInteger(value.size)
        || !Number.isInteger(value.width)
        || !Number.isInteger(value.height)
        || value.size < 1
        || value.width < 1
        || value.height < 1
    ) {
        return null;
    }

    return {
        designMediaUuid: value.designMediaUuid,
        mimeType: value.mimeType,
        size: value.size,
        width: value.width,
        height: value.height,
    };
}

function normalizedModifierOptionUuids(values: readonly string[]): string[] {
    return [...new Set(values.map((value) => value.trim()).filter((value) => value !== ""))];
}

export function publicManufacturingProfileUrl(
    apiBasePath: string,
    productUuid: string,
    variantUuid: string | null,
    modifierOptionUuids: readonly string[],
): string {
    const basePath = apiBasePath.replace(/\/+$/, "");
    const parameters = new URLSearchParams();
    const normalizedVariantUuid = variantUuid?.trim() ?? "";

    if (normalizedVariantUuid !== "") {
        parameters.set("variantUuid", normalizedVariantUuid);
    }

    for (const modifierOptionUuid of normalizedModifierOptionUuids(modifierOptionUuids)) {
        parameters.append("modifierOptionUuid[]", modifierOptionUuid);
    }

    const query = parameters.toString();
    const path = `${basePath}/products/${encodeURIComponent(productUuid)}/manufacturing-profile`;

    return query === "" ? path : `${path}?${query}`;
}

export function publicManufacturingDesignMediaContentUrl(
    cartApiBasePath: string,
    designMediaUuid: string,
): string {
    return `${cartApiBasePath.replace(/\/+$/, "")}/manufacturing-design-media/${encodeURIComponent(designMediaUuid)}`;
}

export function createPublicManufacturingDesignMediaFormData(options: {
    file: Blob;
    fileName: string;
    productUuid: string;
    variantUuid: string | null;
    modifierOptionUuids: readonly string[];
}): FormData {
    const formData = new FormData();

    formData.append("file", options.file, options.fileName);
    formData.append("productUuid", options.productUuid);

    const variantUuid = options.variantUuid?.trim() ?? "";

    if (variantUuid !== "") {
        formData.append("variantUuid", variantUuid);
    }

    for (const modifierOptionUuid of normalizedModifierOptionUuids(options.modifierOptionUuids)) {
        formData.append("modifierOptionUuids[]", modifierOptionUuid);
    }

    return formData;
}

function specificationValueIsValid(key: ManufacturingSpecificationKey, value: string): boolean {
    if (value === "") {
        return false;
    }

    if (key === "designMediaUuid") {
        return DESIGN_MEDIA_UUID_PATTERN.test(value);
    }

    return value.length <= (key === "text" ? 1000 : 100);
}

export function canonicalManufacturingSpecification(
    profile: PublicManufacturingProfile,
    value: PublicManufacturingSpecification,
): PublicManufacturingSpecification {
    if (!profile.profileAvailable) {
        return {};
    }

    const specification: PublicManufacturingSpecification = {};

    for (const key of profile.allowedKeys) {
        const normalized = value[key]?.trim() ?? "";

        if (specificationValueIsValid(key, normalized)) {
            specification[key] = normalized;
        }
    }

    return specification;
}

export function validateManufacturingSpecification(
    profile: PublicManufacturingProfile,
    value: PublicManufacturingSpecification,
): ManufacturingSpecificationValidation {
    if (!profile.profileAvailable) {
        return { invalidKeys: [], missingKeys: [], valid: true };
    }

    const invalidKeys = profile.allowedKeys.filter((key) => {
        const normalized = value[key]?.trim() ?? "";

        return normalized !== "" && !specificationValueIsValid(key, normalized);
    });
    const missingKeys = profile.requiredKeys.filter((key) => {
        const normalized = value[key]?.trim() ?? "";

        return !specificationValueIsValid(key, normalized);
    });

    return {
        invalidKeys,
        missingKeys,
        valid: invalidKeys.length === 0 && missingKeys.length === 0,
    };
}

export function manufacturingSpecificationEntries(value: unknown): Array<readonly [ManufacturingSpecificationKey, string]> {
    if (!isRecord(value)) {
        return [];
    }

    return MANUFACTURING_SPECIFICATION_KEYS.flatMap((key) => {
        const raw = value[key];

        return typeof raw === "string" && raw.trim() !== ""
            ? [[key, raw.trim()] as const]
            : [];
    });
}

export function manufacturingCustomizationContextKey(
    productUuid: string,
    variantUuid: string | null,
    modifierOptionUuids: readonly string[],
): string {
    return JSON.stringify([
        productUuid.trim(),
        variantUuid?.trim() ?? "",
        normalizedModifierOptionUuids(modifierOptionUuids).sort(),
    ]);
}

export function publicCartItemAddBody(options: {
    manufacturingSpecification?: PublicManufacturingSpecification;
    productAttributeUuid: string | null;
    productUuid: string;
    quantity: number;
    surchargeValueUuids: readonly string[];
}): Record<string, unknown> {
    const body: Record<string, unknown> = {
        productUuid: options.productUuid,
        productAttributeUuid: options.productAttributeUuid,
        quantity: options.quantity,
    };

    if (options.surchargeValueUuids.length > 0) {
        body.surchargeValueUuids = [...options.surchargeValueUuids];
    }

    if (options.manufacturingSpecification !== undefined) {
        body.manufacturingSpecification = options.manufacturingSpecification;
    }

    return body;
}

export function publicCartItemUpdateBody(
    quantity: number,
    manufacturingSpecification?: PublicManufacturingSpecification,
): Record<string, unknown> {
    const body: Record<string, unknown> = { quantity };

    if (manufacturingSpecification !== undefined) {
        body.manufacturingSpecification = manufacturingSpecification;
    }

    return body;
}
